import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Receipt,
  Fuel,
  Wrench,
  Circle,
  Hammer,
  Edit,
  Trash2,
  Calendar,
  Truck,
  ScanLine,
  Download,
  Upload,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import AddCostDialog from '../components/AddCostDialog';
import EditCostDialog from '../components/EditCostDialog';
import ScanInvoiceDialog from '../components/ScanInvoiceDialog';
import MonthCompareCard from '../components/expenses/MonthCompareCard';

const COST_TYPE_STYLES = {
  fuel: { icon: Fuel, color: 'bg-orange-100 text-orange-700' },
  service: { icon: Wrench, color: 'bg-blue-100 text-blue-700' },
  maintenance: { icon: Wrench, color: 'bg-emerald-100 text-emerald-700' },
  tyres: { icon: Circle, color: 'bg-purple-100 text-purple-700' },
  labour: { icon: Hammer, color: 'bg-teal-100 text-teal-700' },
  insurance: { icon: Receipt, color: 'bg-amber-100 text-amber-700' },
  license: { icon: Receipt, color: 'bg-pink-100 text-pink-700' },
  other: { icon: Circle, color: 'bg-slate-100 text-slate-700' }
};

const FuelUsageCell = ({ cost }) => {
  if (cost.cost_type !== 'fuel') return <span className="text-slate-400">-</span>;
  const unit = cost.fuel_efficiency_unit || 'km/L';
  const distUnit = unit === 'L/hr' ? 'hrs' : 'km';
  const avg = cost.fuel_running_avg != null && (
    <span className="text-xs text-slate-400 block" data-testid={`expense-fuel-avg-${cost.id}`}>
      avg so far {cost.fuel_running_avg} {unit}
    </span>
  );
  if (cost.fuel_excluded) {
    return (
      <div title="Not counted in the vehicle average">
        <span className="text-xs text-amber-700 font-medium">
          {cost.fuel_efficiency != null ? `${cost.fuel_efficiency} ${unit} · ` : ''}
          {cost.fuel_excluded === 'first fill-up' ? 'First fill-up' : `Not in avg: ${cost.fuel_excluded}`}
        </span>
        {cost.fuel_distance != null && cost.fuel_excluded !== 'first fill-up' && (
          <span className="text-xs text-slate-400 block">{cost.fuel_distance.toLocaleString()} {distUnit}</span>
        )}
        {avg}
      </div>
    );
  }
  if (cost.fuel_efficiency == null) return <span className="text-slate-400">-</span>;
  return (
    <div>
      <span className={`font-data font-medium ${cost.fuel_alert ? 'text-red-700' : 'text-obsidian'}`}>{cost.fuel_efficiency} {unit}</span>
      {cost.fuel_alert && (
        <span className="ml-2 inline-flex items-center gap-1 rounded-sm bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
          title="More than 20% worse than this vehicle's average" data-testid={`expense-fuel-alert-${cost.id}`}>
          <AlertTriangle className="w-3 h-3" />
          {Math.abs(cost.fuel_vs_avg_pct)}% {unit === 'L/hr' ? 'above' : 'below'} avg
        </span>
      )}
      <span className="text-xs text-slate-400 block">
        {cost.fuel_distance?.toLocaleString()} {distUnit} since last fill
      </span>
      {avg}
    </div>
  );
};

const ExpensesPage = () => {
  const [costs, setCosts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costType, setCostType] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await api.downloadCostTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleetcost_expense_template_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await api.uploadCostImport(file);
      const summary = `${result.created} created, ${result.skipped} skipped`;
      if (result.errors?.length) {
        const firstErr = result.errors.slice(0, 3).map(e => `Row ${e.row}: ${e.message}`).join('\n');
        toast.error(`${summary} • ${result.errors.length} error(s)\n${firstErr}`, { duration: 10000 });
      } else {
        toast.success(summary);
      }
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fmtR = (v) => {
    if (v == null || isNaN(v)) return 'R 0.00';
    const n = Number(v);
    const parts = n.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `R ${parts.join('.')}`;
  };

  const fuelAlertCount = costs.filter((c) => c.fuel_alert).length;
  const visibleCosts = alertsOnly && fuelAlertCount > 0 ? costs.filter((c) => c.fuel_alert) : costs;

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (costType !== 'all') params.cost_type = costType;
      if (vehicleFilter !== 'all') params.vehicle_id = vehicleFilter;
      if (monthFilter !== 'all') {
        // monthFilter is 'YYYY-MM'; convert to date range
        params.start_date = `${monthFilter}-01`;
        const [y, m] = monthFilter.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        params.end_date = `${monthFilter}-${String(lastDay).padStart(2, '0')}`;
      }
      
      const [costsData, vehiclesData, suppliersData] = await Promise.all([
        api.getCosts(params),
        api.getVehicles(),
        api.getSuppliers()
      ]);
      setCosts(costsData);
      setVehicles(vehiclesData);
      setSuppliers(suppliersData);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [costType, vehicleFilter, monthFilter]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handleDeleteCost = async () => {
    if (!selectedCost) return;
    try {
      await api.deleteCost(selectedCost.id);
      toast.success('Expense deleted');
      setDeleteDialogOpen(false);
      setSelectedCost(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  // Summary stats
  const totalCost = costs.reduce((sum, c) => sum + c.amount, 0);
  const fuelCost = costs.filter(c => c.cost_type === 'fuel').reduce((sum, c) => sum + c.amount, 0);
  const serviceCost = costs.filter(c => c.cost_type === 'service' || c.cost_type === 'maintenance').reduce((sum, c) => sum + c.amount, 0);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-obsidian tracking-tight">
            Expenses
          </h1>
          <p className="text-slate-500 mt-1">Track and manage all fleet costs</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setScanDialogOpen(true)}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="scan-invoice-btn"
          >
            <ScanLine className="w-4 h-4 mr-2" />
            Scan Invoice
          </Button>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleImportFile}
            className="hidden"
            data-testid="import-file-input"
          />
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-excel-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing…' : 'Import Excel'}
          </Button>
          <Button 
            onClick={() => setCostDialogOpen(true)}
            className="bg-gray-700 hover:bg-gray-600 text-white btn-active"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="overline">Total Expenses</p>
            <p className="font-heading text-2xl font-bold text-obsidian mt-1">
              {fmtR(totalCost)}
            </p>
            <p className="text-sm text-slate-500 mt-1">{costs.length} entries</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="overline">Fuel Costs</p>
            <p className="font-heading text-2xl font-bold text-orange-500 mt-1">
              {fmtR(fuelCost)}
            </p>
            <p className="text-sm text-slate-500 mt-1">{((fuelCost / totalCost) * 100 || 0).toFixed(0)}% of total</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="overline">Service Costs</p>
            <p className="font-heading text-2xl font-bold text-blue-500 mt-1">
              {fmtR(serviceCost)}
            </p>
            <p className="text-sm text-slate-500 mt-1">{((serviceCost / totalCost) * 100 || 0).toFixed(0)}% of total</p>
          </CardContent>
        </Card>
      </div>

      {/* Compare Months (collapsible) */}
      <MonthCompareCard />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={costType} onValueChange={setCostType}>
          <SelectTrigger className="w-[150px]" data-testid="cost-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="fuel">Fuel</SelectItem>
            <SelectItem value="service">Service</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="labour">Labour</SelectItem>
            <SelectItem value="tyres">Tyres</SelectItem>
            <SelectItem value="insurance">Insurance</SelectItem>
            <SelectItem value="license">License</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="vehicle-filter">
            <SelectValue placeholder="All Vehicles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-[180px]" data-testid="month-filter">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {(() => {
              const options = [];
              const now = new Date();
              for (let i = 0; i < 24; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
                options.push(<SelectItem key={value} value={value}>{label}</SelectItem>);
              }
              return options;
            })()}
          </SelectContent>
        </Select>
      </div>

      {fuelAlertCount > 0 && (
        <div className="flex items-center justify-between gap-3 border-2 border-red-200 bg-red-50 rounded-sm px-4 py-3" data-testid="fuel-alerts-banner">
          <div className="flex items-center gap-2 text-sm text-red-800">
            <AlertTriangle className="w-4 h-4" />
            <span>
              <strong data-testid="fuel-alerts-count">{fuelAlertCount}</strong> fill-up{fuelAlertCount === 1 ? '' : 's'} {fuelAlertCount === 1 ? 'is' : 'are'} more than 20% less fuel-efficient than the vehicle's overall average — check for theft, leaks or wrong readings.
            </span>
          </div>
          <Button size="sm" variant="outline" className="border-red-300 text-red-800 hover:bg-red-100"
            onClick={() => setAlertsOnly((v) => !v)} data-testid="fuel-alerts-toggle">
            {alertsOnly ? 'Show all' : 'Show only these'}
          </Button>
        </div>
      )}

      {/* Expenses List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {visibleCosts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Vehicle</th>
                    <th>Odometer / Hrs</th>
                    <th title="Fuel use since the previous fill-up of the same vehicle, and the vehicle average up to that fill-up">Fuel Usage</th>
                    <th>Supplier</th>
                    <th>Invoice</th>
                    <th>Order #</th>
                    <th className="text-right">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCosts.map((cost) => {
                    const typeStyle = COST_TYPE_STYLES[cost.cost_type] || COST_TYPE_STYLES.other;
                    const Icon = typeStyle.icon;
                    return (
                      <tr key={cost.id} data-testid={`expense-row-${cost.id}`} className={cost.fuel_alert ? 'bg-red-50/70' : undefined}>
                        <td>
                          <span className="font-data text-slate-600">{cost.date}</span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded flex items-center justify-center ${typeStyle.color}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="capitalize font-medium">{cost.cost_type}</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-slate-400" />
                            <span>{cost.vehicle_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td>
                          {cost.hours ? (
                            <span className="font-data text-slate-600" data-testid={`expense-hours-${cost.id}`}>{cost.hours.toLocaleString()} Hrs</span>
                          ) : cost.odometer ? (
                            <span className="font-data text-slate-600">{cost.odometer.toLocaleString()} km</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td data-testid={`expense-fuel-usage-${cost.id}`}>
                          <FuelUsageCell cost={cost} />
                        </td>
                        <td className="text-slate-600">
                          {cost.supplier_name || cost.vendor || <span className="text-slate-400">-</span>}
                        </td>
                        <td>
                          {cost.invoice_number ? (
                            <span className="font-data text-xs bg-slate-100 px-2 py-1 rounded">{cost.invoice_number}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td>
                          {cost.order_number ? (
                            <span className="font-data text-xs bg-slate-100 px-2 py-1 rounded" data-testid={`expense-order-${cost.id}`}>{cost.order_number}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="text-right">
                          <span className="font-data font-medium text-obsidian">
                            {fmtR(cost.amount)}
                          </span>
                          {cost.liters && (
                            <span className="text-xs text-slate-400 block">{cost.liters}L @ {fmtR(cost.price_per_liter || (cost.amount/cost.liters))}/L</span>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedCost(cost);
                                setEditDialogOpen(true);
                              }}
                              data-testid={`edit-expense-${cost.id}`}
                            >
                              <Edit className="w-4 h-4 text-slate-400" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedCost(cost);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-expense-${cost.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-slate-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Receipt className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-obsidian mb-2">
                No expenses found
              </h3>
              <p className="text-slate-500 mb-6">
                {costType !== 'all' || vehicleFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Start tracking your fleet expenses'
                }
              </p>
              <Button onClick={() => setCostDialogOpen(true)} className="bg-gray-700 hover:bg-gray-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Cost Dialog */}
      <AddCostDialog 
        open={costDialogOpen} 
        onOpenChange={setCostDialogOpen}
        vehicles={vehicles}
        onSuccess={fetchData}
      />

      {/* Scan Invoice Dialog */}
      <ScanInvoiceDialog
        open={scanDialogOpen}
        onOpenChange={setScanDialogOpen}
        vehicles={vehicles}
        suppliers={suppliers}
        onSuccess={fetchData}
      />

      {/* Edit Cost Dialog */}
      <EditCostDialog 
        open={editDialogOpen} 
        onOpenChange={setEditDialogOpen}
        cost={selectedCost}
        onSuccess={fetchData}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCost} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-expense-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesPage;
